// Admin Page Entry Point
import './styles/admin.scss';
import { API_URL } from './utils/ui-helpers.js';

console.log('MCP Admin loaded');

interface MissingVideo {
  videoId: string;
  videoPath: string;
  inputImagePath: string;
  metadata: {
    modelName: string;
    provider: string;
    duration: number;
    aspectRatio: string;
    enableAudio: boolean;
    timestamp: number;
    cost: number;
    generationTime: number;
    fileSizeBytes: number;
    prompt: string;
    videoFile: string;
    inputImage: string;
  };
}

interface ScanResponse {
  success: boolean;
  missing: MissingVideo[];
  error?: string;
}

interface RepairResponse {
  success: boolean;
  repaired: number;
  failed: number;
  details: Array<{
    videoId: string;
    success: boolean;
    error?: string;
  }>;
  error?: string;
}

interface PrefabPrompt {
  id: string;
  name: string;
  prompt: string;
  category: string;
  description?: string;
  createdAt: number;
  updatedAt: number;
  sortOrder: number;
}

interface PromptsResponse {
  success: boolean;
  prompts: PrefabPrompt[];
  error?: string;
}

interface CategoriesResponse {
  success: boolean;
  categories: string[];
  error?: string;
}

let missingVideos: MissingVideo[] = [];
let currentEditingPromptId: string | null = null;

void (async function () {
  try {
    await window.initAuth();
    const mainContainer = document.getElementById('mainContainer');
    if (mainContainer) {
      mainContainer.style.display = 'block';
    }
    window.showUserInfo();
    initAdminPage();
  } catch (err: unknown) {
    console.error('Failed to initialize:', err);
  }
})();

function initAdminPage(): void {
  loadDbStats();
  void loadPrefabPrompts();
  void loadCategories();

  const scanBtn = document.getElementById('scanVideosBtn');
  const repairBtn = document.getElementById('repairVideosBtn');

  scanBtn?.addEventListener('click', () => void scanMissingVideos());
  repairBtn?.addEventListener('click', () => void repairMissingVideos());

  // Prefab prompts event listeners
  const newPromptBtn = document.getElementById('newPromptBtn');
  const categoryFilter = document.getElementById('categoryFilter');
  const modalClose = document.getElementById('modalClose');
  const modalCancel = document.getElementById('modalCancel');
  const promptForm = document.getElementById('promptForm');
  const deleteModalClose = document.getElementById('deleteModalClose');
  const deleteModalCancel = document.getElementById('deleteModalCancel');

  newPromptBtn?.addEventListener('click', openNewPromptModal);
  categoryFilter?.addEventListener('change', () => void loadPrefabPrompts());
  modalClose?.addEventListener('click', closePromptModal);
  modalCancel?.addEventListener('click', closePromptModal);
  promptForm?.addEventListener('submit', handlePromptSubmit);
  deleteModalClose?.addEventListener('click', closeDeleteModal);
  deleteModalCancel?.addEventListener('click', closeDeleteModal);
}

async function loadDbStats(): Promise<void> {
  try {
    const response = await (window as any).authenticatedFetch(
      `${API_URL}/api/history/stats`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const statsGrid = document.getElementById('dbStats');

    if (statsGrid && data.stats) {
      statsGrid.innerHTML = `
        <div class="stat-card">
          <div class="stat-value">${data.stats.total || 0}</div>
          <div class="stat-label">Totaal Generaties</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">$${(data.stats.totalCost || 0).toFixed(2)}</div>
          <div class="stat-label">Totale Kosten</div>
        </div>
        ${(data.stats.byProvider || [])
          .map(
            (p: { provider: string; count: number }) => `
          <div class="stat-card">
            <div class="stat-value">${p.count}</div>
            <div class="stat-label">${p.provider}</div>
          </div>
        `
          )
          .join('')}
      `;
    }
  } catch (error) {
    console.error('Failed to load stats:', error);
    const statsGrid = document.getElementById('dbStats');
    if (statsGrid) {
      statsGrid.innerHTML =
        '<p style="color: #e74c3c;">Fout bij laden van statistieken</p>';
    }
  }
}

async function scanMissingVideos(): Promise<void> {
  const scanBtn = document.getElementById('scanVideosBtn') as HTMLButtonElement;
  const repairBtn = document.getElementById(
    'repairVideosBtn'
  ) as HTMLButtonElement;
  const resultsBox = document.getElementById('scanResults');
  const resultsContent = document.getElementById('scanResultsContent');

  if (!scanBtn || !repairBtn || !resultsBox || !resultsContent) return;

  scanBtn.disabled = true;
  scanBtn.textContent = 'Scannen...';
  repairBtn.disabled = true;

  try {
    const response = await (window as any).authenticatedFetch(
      `${API_URL}/api/admin/scan-videos`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ScanResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Scan failed');
    }

    missingVideos = data.missing;
    resultsBox.style.display = 'block';

    if (missingVideos.length === 0) {
      resultsContent.innerHTML = `
        <p style="color: #27ae60; font-weight: 500;">
          ✓ Alle videos hebben een database record
        </p>
      `;
      repairBtn.disabled = true;
    } else {
      resultsContent.innerHTML = `
        <p style="color: #e67e22; font-weight: 500; margin-bottom: 1rem;">
          ⚠ Gevonden: ${missingVideos.length} video(s) zonder database record
        </p>
        <ul style="list-style: none; padding: 0;">
          ${missingVideos
            .map(
              (v) => `
            <li style="padding: 0.5rem; background: #f8f9fa; margin-bottom: 0.5rem; border-radius: 4px; font-family: monospace; font-size: 0.9rem;">
              ${v.videoId}
              <div style="font-size: 0.85rem; color: #666; margin-top: 0.25rem;">
                ${v.metadata.prompt?.substring(0, 60)}${v.metadata.prompt?.length > 60 ? '...' : ''}
              </div>
            </li>
          `
            )
            .join('')}
        </ul>
      `;
      repairBtn.disabled = false;
    }
  } catch (error) {
    console.error('Scan failed:', error);
    resultsBox.style.display = 'block';
    resultsContent.innerHTML = `
      <p style="color: #e74c3c;">
        ✗ Scan mislukt: ${error instanceof Error ? error.message : 'Unknown error'}
      </p>
    `;
  } finally {
    scanBtn.disabled = false;
    scanBtn.textContent = 'Scan Missing Videos';
  }
}

async function repairMissingVideos(): Promise<void> {
  const repairBtn = document.getElementById(
    'repairVideosBtn'
  ) as HTMLButtonElement;
  const resultsBox = document.getElementById('repairResults');
  const resultsContent = document.getElementById('repairResultsContent');

  if (!repairBtn || !resultsBox || !resultsContent) return;

  if (
    !confirm(
      `Weet je zeker dat je ${missingVideos.length} video record(s) wilt toevoegen aan de database?`
    )
  ) {
    return;
  }

  repairBtn.disabled = true;
  repairBtn.textContent = 'Repareren...';

  try {
    const response = await (window as any).authenticatedFetch(
      `${API_URL}/api/admin/repair-videos`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videos: missingVideos }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: RepairResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Repair failed');
    }

    resultsBox.style.display = 'block';
    resultsContent.innerHTML = `
      <p style="color: #27ae60; font-weight: 500; margin-bottom: 1rem;">
        ✓ Reparatie voltooid
      </p>
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1rem;">
        <div class="stat-card">
          <div class="stat-value" style="color: #27ae60;">${data.repaired}</div>
          <div class="stat-label">Succesvol</div>
        </div>
        <div class="stat-card">
          <div class="stat-value" style="color: #e74c3c;">${data.failed}</div>
          <div class="stat-label">Mislukt</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${data.details.length}</div>
          <div class="stat-label">Totaal</div>
        </div>
      </div>
      ${
        data.failed > 0
          ? `
        <details>
          <summary style="cursor: pointer; font-weight: 500; margin-bottom: 0.5rem;">Fouten bekijken</summary>
          <ul style="list-style: none; padding: 0; margin-top: 0.5rem;">
            ${data.details
              .filter((d) => !d.success)
              .map(
                (d) => `
              <li style="padding: 0.5rem; background: #fee; margin-bottom: 0.5rem; border-radius: 4px; font-size: 0.9rem;">
                ${d.videoId}: ${d.error}
              </li>
            `
              )
              .join('')}
          </ul>
        </details>
      `
          : ''
      }
    `;

    // Reload stats
    void loadDbStats();

    // Clear missing videos list
    missingVideos = [];
    repairBtn.disabled = true;
  } catch (error) {
    console.error('Repair failed:', error);
    resultsBox.style.display = 'block';
    resultsContent.innerHTML = `
      <p style="color: #e74c3c;">
        ✗ Reparatie mislukt: ${error instanceof Error ? error.message : 'Unknown error'}
      </p>
    `;
  } finally {
    repairBtn.disabled = false;
    repairBtn.textContent = 'Repareer Videos';
  }
}

// ============================================================
// Prefab Prompts CRUD
// ============================================================

async function loadPrefabPrompts(): Promise<void> {
  const loading = document.getElementById('promptsLoading');
  const container = document.getElementById('promptsContainer');
  const tableBody = document.getElementById('promptsTableBody');
  const categoryFilter = document.getElementById(
    'categoryFilter'
  ) as HTMLSelectElement | null;

  if (!loading || !container || !tableBody) return;

  loading.style.display = 'block';
  container.style.display = 'none';

  try {
    const category = categoryFilter?.value || '';
    const url = category
      ? `${API_URL}/api/admin/prefab-prompts?category=${encodeURIComponent(category)}`
      : `${API_URL}/api/admin/prefab-prompts`;

    const response = await (window as any).authenticatedFetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: PromptsResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to load prompts');
    }

    // Render prompts table
    tableBody.innerHTML = data.prompts
      .map(
        (p) => `
      <tr>
        <td>${escapeHtml(p.name)}</td>
        <td><span class="badge">${escapeHtml(p.category)}</span></td>
        <td class="prompt-text">${escapeHtml(p.prompt.substring(0, 80))}${p.prompt.length > 80 ? '...' : ''}</td>
        <td>${p.sortOrder}</td>
        <td>
          <button class="btn-icon" onclick="editPrompt('${p.id}')" title="Bewerken">
            ✏️
          </button>
          <button class="btn-icon btn-danger" onclick="deletePrompt('${p.id}', '${escapeHtml(p.name)}')" title="Verwijderen">
            🗑️
          </button>
        </td>
      </tr>
    `
      )
      .join('');

    if (data.prompts.length === 0) {
      tableBody.innerHTML = `
        <tr>
          <td colspan="5" style="text-align: center; color: #666; padding: 2rem;">
            Geen prompts gevonden
          </td>
        </tr>
      `;
    }

    container.style.display = 'block';
  } catch (error) {
    console.error('Failed to load prompts:', error);
    tableBody.innerHTML = `
      <tr>
        <td colspan="5" style="text-align: center; color: #e74c3c; padding: 2rem;">
          Fout bij laden van prompts: ${error instanceof Error ? error.message : 'Unknown error'}
        </td>
      </tr>
    `;
    container.style.display = 'block';
  } finally {
    loading.style.display = 'none';
  }
}

async function loadCategories(): Promise<void> {
  try {
    const response = await (window as any).authenticatedFetch(
      `${API_URL}/api/admin/prefab-prompts/categories`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: CategoriesResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to load categories');
    }

    // Update category filter
    const categoryFilter = document.getElementById(
      'categoryFilter'
    ) as HTMLSelectElement | null;
    if (categoryFilter) {
      const currentValue = categoryFilter.value;
      categoryFilter.innerHTML = '<option value="">Alle categorieën</option>';
      data.categories.forEach((cat) => {
        const option = document.createElement('option');
        option.value = cat;
        option.textContent = cat;
        categoryFilter.appendChild(option);
      });
      categoryFilter.value = currentValue;
    }

    // Update category select in modal
    const promptCategory = document.getElementById(
      'promptCategory'
    ) as HTMLSelectElement | null;
    if (promptCategory) {
      const currentValue = promptCategory.value;
      promptCategory.innerHTML =
        '<option value="">-- Selecteer categorie --</option>';
      data.categories.forEach((cat) => {
        const option = document.createElement('option');
        option.value = cat;
        option.textContent = cat;
        promptCategory.appendChild(option);
      });
      // Add "Nieuwe categorie" option
      const newOption = document.createElement('option');
      newOption.value = '_new_';
      newOption.textContent = '+ Nieuwe categorie...';
      promptCategory.appendChild(newOption);
      promptCategory.value = currentValue;
    }
  } catch (error) {
    console.error('Failed to load categories:', error);
  }
}

function openNewPromptModal(): void {
  currentEditingPromptId = null;
  const modal = document.getElementById('promptModal');
  const modalTitle = document.getElementById('modalTitle');
  const form = document.getElementById('promptForm') as HTMLFormElement | null;

  if (!modal || !modalTitle || !form) return;

  modalTitle.textContent = 'Nieuwe Prompt';
  form.reset();
  modal.style.display = 'flex';
}

(window as any).editPrompt = async function (id: string): Promise<void> {
  try {
    const response = await (window as any).authenticatedFetch(
      `${API_URL}/api/admin/prefab-prompts`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: PromptsResponse = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to load prompts');
    }

    const prompt = data.prompts.find((p) => p.id === id);
    if (!prompt) {
      alert('Prompt niet gevonden');
      return;
    }

    currentEditingPromptId = id;
    const modal = document.getElementById('promptModal');
    const modalTitle = document.getElementById('modalTitle');
    const promptName = document.getElementById(
      'promptName'
    ) as HTMLInputElement;
    const promptCategory = document.getElementById(
      'promptCategory'
    ) as HTMLSelectElement;
    const promptText = document.getElementById(
      'promptText'
    ) as HTMLTextAreaElement;
    const promptDescription = document.getElementById(
      'promptDescription'
    ) as HTMLTextAreaElement;
    const promptSortOrder = document.getElementById(
      'promptSortOrder'
    ) as HTMLInputElement;

    if (
      !modal ||
      !modalTitle ||
      !promptName ||
      !promptCategory ||
      !promptText ||
      !promptDescription ||
      !promptSortOrder
    )
      return;

    modalTitle.textContent = 'Prompt Bewerken';
    promptName.value = prompt.name;
    promptCategory.value = prompt.category;
    promptText.value = prompt.prompt;
    promptDescription.value = prompt.description || '';
    promptSortOrder.value = prompt.sortOrder.toString();
    modal.style.display = 'flex';
  } catch (error) {
    console.error('Failed to load prompt:', error);
    alert(
      `Fout bij laden van prompt: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

(window as any).deletePrompt = function (id: string, name: string): void {
  const modal = document.getElementById('deleteModal');
  const promptName = document.getElementById('deletePromptName');
  const confirmBtn = document.getElementById('deleteModalConfirm');

  if (!modal || !promptName || !confirmBtn) return;

  promptName.textContent = name;
  modal.style.display = 'flex';

  // Remove old event listener and add new one
  const newConfirmBtn = confirmBtn.cloneNode(true) as HTMLElement;
  confirmBtn.parentNode?.replaceChild(newConfirmBtn, confirmBtn);

  newConfirmBtn.addEventListener('click', () => void confirmDelete(id));
};

function closePromptModal(): void {
  const modal = document.getElementById('promptModal');
  if (modal) modal.style.display = 'none';
  currentEditingPromptId = null;
}

function closeDeleteModal(): void {
  const modal = document.getElementById('deleteModal');
  if (modal) modal.style.display = 'none';
}

async function handlePromptSubmit(e: Event): Promise<void> {
  e.preventDefault();

  const form = e.target as HTMLFormElement;
  const formData = new FormData(form);

  let category = formData.get('category') as string;
  const name = formData.get('name') as string;
  const prompt = formData.get('prompt') as string;
  const description = formData.get('description') as string;
  const sortOrder = parseInt(formData.get('sortOrder') as string) || 0;

  // Handle new category
  if (category === '_new_') {
    const newCategory = window.prompt('Voer nieuwe categorie naam in:');
    if (!newCategory || !newCategory.trim()) {
      alert('Categorie naam is verplicht');
      return;
    }
    category = newCategory.trim();
  }

  const submitBtn = document.getElementById('modalSubmit') as HTMLButtonElement;
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = 'Opslaan...';
  }

  try {
    let url = `${API_URL}/api/admin/prefab-prompts`;
    let method = 'POST';

    if (currentEditingPromptId) {
      url += `/${currentEditingPromptId}`;
      method = 'PUT';
    }

    const response = await (window as any).authenticatedFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        prompt,
        category,
        description: description || undefined,
        sortOrder,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to save prompt');
    }

    closePromptModal();
    void loadPrefabPrompts();
    void loadCategories();

    alert(
      currentEditingPromptId
        ? 'Prompt succesvol bijgewerkt'
        : 'Prompt succesvol aangemaakt'
    );
  } catch (error) {
    console.error('Failed to save prompt:', error);
    alert(
      `Fout bij opslaan: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Opslaan';
    }
  }
}

async function confirmDelete(id: string): Promise<void> {
  const confirmBtn = document.getElementById(
    'deleteModalConfirm'
  ) as HTMLButtonElement;

  if (confirmBtn) {
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Verwijderen...';
  }

  try {
    const response = await (window as any).authenticatedFetch(
      `${API_URL}/api/admin/prefab-prompts/${id}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete prompt');
    }

    closeDeleteModal();
    void loadPrefabPrompts();
    void loadCategories();

    alert('Prompt succesvol verwijderd');
  } catch (error) {
    console.error('Failed to delete prompt:', error);
    alert(
      `Fout bij verwijderen: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  } finally {
    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Verwijderen';
    }
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
